/** Logo Chat — bulles seules, viewBox serré (pas de zone carrée vide). */
export function ChatLogoMark({ className = "", title }: { className?: string; title?: string }) {
  return (
    <svg
      className={className}
      viewBox="4 5 37 33"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role={title ? "img" : "presentation"}
      aria-hidden={title ? undefined : true}
    >
      {title ? <title>{title}</title> : null}
      {/* Bulle arrière */}
      <path
        fill="#1E88E5"
        d="M18.5 14h16c2.5 0 4.5 2 4.5 4.5v9c0 2.5-2 4.5-4.5 4.5H28l-5.2 4.2c-.55.45-1.35.05-1.35-.65V32H18.5C16 32 14 30 14 27.5v-9c0-2.5 2-4.5 4.5-4.5Z"
      />
      {/* Bulle avant */}
      <path
        fill="#4FC3F7"
        d="M9.5 6h16C28 6 30 8 30 10.5v9c0 2.5-2 4.5-4.5 4.5H18l-5.2 4.2c-.55.45-1.35.05-1.35-.65V24H9.5C7 24 5 22 5 19.5v-9C5 8 7 6 9.5 6Z"
      />
    </svg>
  );
}

/** Splash : bulles en contour dans un cercle à la couleur du thème. */
export function ChatSplashMark({ className = "", title }: { className?: string; title?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role={title ? "img" : "presentation"}
      aria-hidden={title ? undefined : true}
    >
      {title ? <title>{title}</title> : null}
      <defs>
        <linearGradient id="chatBootGrad" x1="8" y1="4" x2="40" y2="44" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="var(--chat-accent-strong, #2b7fd4)" />
          <stop offset="1" stopColor="color-mix(in srgb, var(--chat-accent, #71baff) 55%, white)" />
        </linearGradient>
      </defs>
      <circle className="chat-boot__disc" cx="24" cy="24" r="24" fill="url(#chatBootGrad)" />
      <path
        className="chat-boot__bubble-back"
        pathLength="1"
        stroke="#F7F4EE"
        strokeWidth="1.5"
        strokeLinejoin="round"
        d="M22 15.2h11.2c1.7 0 3 1.3 3 3v6.2c0 1.7-1.3 3-3 3H29l-3.4 2.6c-.4.3-.9 0-.9-.45V27.4H22c-1.7 0-3-1.3-3-3v-6.2c0-1.7 1.3-3 3-3Z"
      />
      <path
        className="chat-boot__bubble"
        pathLength="1"
        stroke="#F7F4EE"
        strokeWidth="1.6"
        strokeLinejoin="round"
        d="M14.2 12.2h12.4c1.8 0 3.2 1.4 3.2 3.2v6.6c0 1.8-1.4 3.2-3.2 3.2H21l-3.6 2.8c-.4.3-1 0-1-.45V25.2h-2.2c-1.8 0-3.2-1.4-3.2-3.2v-6.6c0-1.8 1.4-3.2 3.2-3.2Z"
      />
      <circle className="chat-boot__seal" cx="31.8" cy="30.4" r="4.2" fill="#F7F4EE" />
      <path
        className="chat-boot__tick"
        d="M29.8 30.4 31.2 31.9 34 29"
        pathLength="1"
        stroke="var(--chat-accent-strong, #2b7fd4)"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

type Props = {
  className?: string;
  size?: number | string;
  alt?: string;
  /** Cercle animé du splash. Le logo dans l’app reste les bulles. */
  splash?: boolean;
};

export function ChatLogo({ className = "", size = 72, alt = "Aurélia Chat", splash = false }: Props) {
  const dim = typeof size === "number" ? `${size}px` : size;
  return (
    <span
      className={`chat-logo ${className}`.trim()}
      style={{ width: dim, height: splash ? dim : `calc(${dim} * 0.9)` }}
      role="img"
      aria-label={alt || undefined}
    >
      {splash ? (
        <ChatSplashMark className="chat-logo__svg" title={alt || undefined} />
      ) : (
        <ChatLogoMark className="chat-logo__svg" title={alt || undefined} />
      )}
    </span>
  );
}

/** Marque hero : logo tuile + titre (écran vide / marque). */
export function ChatBrandMark({ size = "lg" }: { size?: "sm" | "lg" }) {
  const logo = size === "lg" ? 72 : 44;
  const title = size === "lg" ? "text-xl" : "text-sm";
  return (
    <div className={`chat-brand-mark flex flex-col items-center gap-2 ${size === "lg" ? "py-2" : ""}`}>
      <span className="chat-brand-icon relative flex items-center justify-center">
        <ChatLogo size={logo} />
      </span>
      <div className="text-center">
        <p className={`${title} font-semibold tracking-tight text-[var(--chat-text)]`}>Aurélia Chat</p>
        {size === "lg" ? (
          <p className="mt-0.5 text-xs text-[var(--chat-muted)]">Messagerie de la suite</p>
        ) : null}
      </div>
    </div>
  );
}
